import * as React from 'react';
import Payment from 'payment';
import * as DateFns from 'date-fns';
import ErrorMessage from '../components/ErrorMessage';

export type CardType =
  | 'VISA'
  | 'MASTERCARD'
  | 'AMEX'
  | 'DISCOVER'
  | 'JCB'
  | 'DINERSCLUB'
  | 'MAESTRO'
  | 'UNIONPAY'
  | 'ELO';

export type RuleNameType =
  | 'required'
  | 'name'
  | 'before'
  | 'after'
  | 'unAvailableDates'
  | 'minLength'
  | 'equal'
  | 'notEqual'
  | 'notDuplicate'
  | 'oneOf'
  | 'noneOf'
  | 'numeric'
  | 'zipCode'
  | 'poBox'
  | 'notPOBox'
  | 'phone'
  | 'email'
  | 'date'
  | 'cardNumber'
  | 'cardType'
  | 'cardExp'
  | 'cvv'
  | 'flag'
  | 'checkForTrue'
  | 'fileType'
  | 'fileSize'
  | 'password'
  | 'minValue'
  | 'maxValue'
  | 'empty';

export type Rule = {
  ruleName: RuleNameType;
  ruleValue?: boolean | number | string | CardType[] | any;
  format?: string;
  dependsOn?: string[];
  defaultMessage?: string;
  messageLanguage?: string;
  message?: React.ReactNode;
};

export interface ValidationRules {
  fieldName: string;
  fieldValue?: any;
  rules: Rule[];
}

export interface Validator {
  validationRules: ValidationRules[];
}

function validateComponent(
  component: any,
  validator: Validator,
  validationInput?: any,
  doNotSetResponseInState?: boolean
) {
  const validationResponse = validate(
    validationInput || component.state,
    validator
  );
  if (!doNotSetResponseInState) {
    component.setState({
      ...component.state,
      validationResponse,
      complete: validationResponse.allValid
    });
  }
  return validationResponse.allValid;
}

function hasError(component: any, fieldName: string) {
  return (
    component &&
    component.state &&
    component.state.validationResponse &&
    hasErrorInField(fieldName, component.state.validationResponse)
  );
}

function getErrors(component: any) {
  return (
    component &&
    component.state &&
    component.state.validationResponse &&
    component.state.validationResponse.errors
  );
}

function isAllValid(component: any) {
  return (
    component &&
    component.state &&
    component.state.validationResponse &&
    component.state.validationResponse.allValid
  );
}

export type RulesMessages = {
  required?: React.ReactNode;
  name?: React.ReactNode;
  before?: React.ReactNode;
  after?: React.ReactNode;
  unAvailableDates?: React.ReactNode;
  minLength?: React.ReactNode;
  equal?: React.ReactNode;
  notEqual?: React.ReactNode;
  notDuplicate?: React.ReactNode;
  oneOf?: React.ReactNode;
  noneOf?: React.ReactNode;
  numeric?: React.ReactNode;
  zipCode?: React.ReactNode;
  poBox?: React.ReactNode;
  notPOBox?: React.ReactNode;
  phone?: React.ReactNode;
  email?: React.ReactNode;
  date?: React.ReactNode;
  cardNumber?: React.ReactNode;
  cardType?: React.ReactNode;
  cardExp?: React.ReactNode;
  cvv?: React.ReactNode;
  flag?: React.ReactNode;
  checkForTrue?: React.ReactNode;
  fileType?: React.ReactNode;
  fileSize?: React.ReactNode;
  password?: React.ReactNode;
  minValue?: React.ReactNode;
  maxValue?: React.ReactNode;
  empty?: React.ReactNode;
};

export interface hasErrorFunction {
  (fieldName: string): boolean;
}

export interface ValidationResponse {
  allValid: boolean;
  errorFields?: string[];
  errors?: any[];
  hasError?: hasErrorFunction;
}

function validate(
  validationInput: any,
  validator: Validator
): ValidationResponse {
  const validationResponse: ValidationResponse = {
    allValid: true,
    errorFields: [],
    errors: [],
    hasError(fieldName: string): boolean {
      return (
        this.errorFields !== undefined && this.errorFields.includes(fieldName)
      );
    }
  };

  validator.validationRules.forEach(validationRulesOnField => {
    const rulesNotFailed: string[] = [];
    validationRulesOnField.rules.forEach(validationRuleOnField => {
      if (
        !validationRuleOnField.dependsOn ||
        validationRuleOnField.dependsOn.every(elem =>
          rulesNotFailed.includes(elem)
        )
      ) {
        if (
          !validateRule(
            validationRuleOnField,
            validationInput,
            validationRulesOnField.fieldName,
            validationRulesOnField.fieldValue
          )
        ) {
          validationResponse.allValid = false;
          if (validationResponse.errorFields) {
            validationResponse.errorFields.push(
              validationRulesOnField.fieldName
            );
          }
          if (validationResponse.errors) {
            validationResponse.errors.push(
              <ErrorMessage
                key={`ErrorMessage${validationRuleOnField.defaultMessage}`}
                defaultMessage={validationRuleOnField.defaultMessage}
                message={validationRuleOnField.message}
              />
            );
          }
        } else {
          rulesNotFailed.push(validationRuleOnField.ruleName);
        }
      }
    });
  });
  return validationResponse;
}

function hasErrorInField(
  fieldName: string,
  validationResponse: ValidationResponse
) {
  return (
    validationResponse &&
    !validationResponse.allValid &&
    validationResponse.hasError &&
    validationResponse.hasError(fieldName)
  );
}

function validateRule(
  rule: Rule,
  inputObject: any,
  fieldName: string,
  fieldCustomValue?: any
): boolean {
  const fieldValue: any =
    fieldCustomValue !== undefined ? fieldCustomValue : inputObject[fieldName];
  let compareValue = rule.ruleValue;
  switch (rule.ruleName) {
    case 'required':
      return requiredRule(fieldValue);
    case 'before':
      return beforeRule(fieldValue, compareValue, rule.format);
    case 'after':
      return afterRule(fieldValue, compareValue, rule.format);
    case 'unAvailableDates':
      return unAvailableDatesRule(fieldValue, compareValue, rule.format);
    case 'minLength':
      return minLengthRule(fieldValue, parseInt(compareValue, 10));
    case 'notDuplicate':
      return notDuplicate(fieldValue, compareValue);
    case 'oneOf':
      return oneOf(fieldValue, compareValue);
    case 'noneOf':
      return noneOf(fieldValue, compareValue);
    case 'equal': {
      if (inputObject[rule.ruleValue]) {
        compareValue = inputObject[rule.ruleValue];
      }
      return equalRule(fieldValue, String(compareValue));
    }
    case 'notEqual': {
      if (inputObject[rule.ruleValue]) {
        compareValue = inputObject[rule.ruleValue];
      }
      return notEqualRule(fieldValue, String(compareValue));
    }
    case 'fileType': {
      return fileTypeRule(fieldValue, compareValue);
    }
    case 'fileSize': {
      return fileSizeRule(fieldValue, compareValue);
    }
    case 'phone':
      return phoneRule(fieldValue);
    case 'email':
      return emailRule(fieldValue);
    case 'date':
      return dateRule(fieldValue, rule.format || 'MM/dd/yyyy');
    case 'name':
      return nameRule(fieldValue);
    case 'numeric':
      return numericRule(fieldValue);
    case 'zipCode':
      return zipCodeRule(fieldValue);
    case 'poBox':
      return !postBoxRule(fieldValue);
    case 'notPOBox':
      return postBoxRule(fieldValue);
    case 'cardExp':
      return cardExpRule(fieldValue);
    case 'cardNumber':
      return cardNumberRule(fieldValue);
    case 'cardType': {
      const cardTypes: CardType[] = rule.ruleValue;
      return cardTypeRule(fieldValue, cardTypes);
    }
    case 'checkForTrue':
      return checkForTrueRule(fieldValue);
    case 'password':
      return passwordRule(fieldValue);
    case 'minValue':
      return minValueRule(fieldValue, compareValue);
    case 'maxValue':
      return maxValueRule(fieldValue, compareValue);
    case 'empty':
      return emptyRule(fieldValue);
    default:
      return true;
  }
}

function requiredRule(fieldValue: string): boolean {
  return !(
    fieldValue === undefined ||
    fieldValue === null ||
    fieldValue.trim() === '' ||
    fieldValue.trim() === '-1'
  );
}

function minLengthRule(fieldValue: string, minLength: number): boolean {
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    fieldValue.trim().length < minLength
  );
}

function notDuplicate(fieldValue: string, compareValue: string[]): boolean {
  const copiedVal = [...compareValue];
  const index = compareValue.indexOf(fieldValue);
  copiedVal.splice(index, index > -1 ? 1 : 0);
  return (
    fieldValue !== undefined &&
    fieldValue !== null &&
    compareValue !== undefined &&
    compareValue !== null &&
    !(compareValue.includes(fieldValue) && copiedVal.includes(fieldValue))
  );
}

function oneOf(fieldValue: string, compareValue: string[]): boolean {
  return (
    fieldValue !== undefined &&
    fieldValue !== null &&
    compareValue !== undefined &&
    compareValue !== null &&
    compareValue.includes(fieldValue)
  );
}

function noneOf(fieldValue: string, compareValue: string[]): boolean {
  return (
    fieldValue === undefined ||
    fieldValue === null ||
    (compareValue !== undefined &&
      compareValue !== null &&
      !compareValue.includes(fieldValue))
  );
}

function equalRule(fieldValue: string, compareValue: string): boolean {
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    fieldValue.trim() !== compareValue
  );
}

function notEqualRule(fieldValue: string, compareValue: string): boolean {
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    fieldValue.trim() === compareValue
  );
}

function fileTypeRule(fieldValue: any, compareValue: string[]): boolean {
  return (
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.type !== '' &&
    compareValue
      .map(ext => ext.toLowerCase())
      .includes(
        fieldValue.type
          .split('/')
          .pop()
          .toLowerCase()
      )
  );
}

function fileSizeRule(fieldValue: any, compareValue: number): boolean {
  return (
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.size !== '' &&
    compareValue > fieldValue.size / 1024
  );
}

function phoneRule(fieldValue: string): boolean {
  const regex = /^[\\(]{0,1}([0-9]){3}[\\)]{0,1}[ ]?([^0-1]){1}([0-9]){2}[ ]?[-]?[ ]?([0-9]){4}[ ]*((x){0,1}([0-9]){1,5}){0,1}$/;
  return (
    fieldValue.length === 0 ||
    (fieldValue.length >= 10 &&
      fieldValue.length <= 14 &&
      regex.test(fieldValue))
  );
}

function emailRule(fieldValue: string): boolean {
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    !regex.test(fieldValue)
  );
}

function dateRule(fieldValue: string, dateFormat: string): boolean {
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    !isValidDate(fieldValue, dateFormat)
  );
}

function beforeRule(
  fieldValue: string,
  ruleValue: Date,
  dateFormat?: string
): boolean {
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    isValidDate(fieldValue, dateFormat || 'MM/dd/yyyy') &&
    toDateObject(fieldValue, dateFormat || 'MM/dd/yyyy') > ruleValue
  );
}

function afterRule(
  fieldValue: string,
  ruleValue: Date,
  dateFormat?: string
): boolean {
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    isValidDate(fieldValue, dateFormat || 'MM/dd/yyyy') &&
    toDateObject(fieldValue, dateFormat || 'MM/dd/yyyy') < ruleValue
  );
}

function unAvailableDatesRule(
  fieldValue: string,
  ruleValue: Date[],
  dateFormat?: string
): boolean {
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    isValidDate(fieldValue, dateFormat || 'MM/dd/yyyy') &&
    ruleValue.find(d => {
      return (
        d.toDateString() ===
        toDateObject(fieldValue, dateFormat || 'MM/dd/yyyy').toDateString()
      );
    })
  );
}

function toDateObject(_fieldValue: string, _dateFormat: string): Date {
  const fieldValue = _fieldValue.replace(/ /g, '');
  const dateFormat = _dateFormat.replace(/ /g, '');
  if (dateFormat === 'MM/dd/yyyy' || dateFormat === 'MM/dd/yy') {
    return new Date(fieldValue);
  }
  if (dateFormat === 'MM/yyyy') {
    return DateFns.lastDayOfMonth(
      new Date(`${fieldValue.slice(0, 3)}01/${fieldValue.slice(3)}`)
    );
  }
  if (dateFormat === 'MM/yy') {
    return DateFns.lastDayOfMonth(
      new Date(`${fieldValue.slice(0, 3)}01/20${fieldValue.slice(3)}`)
    );
  }

  return new Date();
}

function isValidDate(_fieldValue: string, _dateFormat: string): boolean {
  const fieldValue = _fieldValue.replace(/ /g, '');
  const dateFormat = _dateFormat.replace(/ /g, '');
  let parts: any;
  let day: any;
  let month: any;
  let year: any;
  const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (dateFormat === 'MM/dd/yyyy') {
    // First check for the pattern
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(fieldValue)) return false;

    // Parse the date parts to integers
    parts = fieldValue.split('/');
    day = parseInt(parts[1], 10);
    month = parseInt(parts[0], 10);
    year = parseInt(parts[2], 10);

    // Check the ranges of month and year
    if (year < 1000 || year > 3000 || month === 0 || month > 12) return false;

    // monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Adjust for leap years
    if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0))
      monthLength[1] = 29;

    // Check the range of the day
    return day > 0 && day <= monthLength[month - 1];
  }
  if (dateFormat === 'MM/dd/yy') {
    if (!/^\d{2}\/\d{2}\/\d{2}$/.test(fieldValue)) return false;
    parts = fieldValue.split('/');
    day = parseInt(parts[1], 10);
    month = parseInt(parts[0], 10);
    year = parseInt(parts[2], 10);

    if (year < 0o0 || year > 99 || month === 0 || month > 12) return false;
    // const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day > 0 && day <= monthLength[month - 1];
  }
  if (dateFormat === 'MM/yy') {
    if (!/^\d{2}\/\d{2}$/.test(fieldValue)) return false;
    parts = fieldValue.split('/');
    month = parseInt(parts[0], 10);
    year = parseInt(parts[1], 10);
    return year > 0o0 && year <= 99 && month > 0o0 && month <= 12;
  }
  if (dateFormat === 'MM/yyyy') {
    if (!/^\d{2}\/\d{4}$/.test(fieldValue)) return false;
    parts = fieldValue.split('/');
    month = parseInt(parts[0], 10);
    year = parseInt(parts[1], 10);
    return (year < 1000 && year > 3000) || (month > 0o0 && month <= 12);
  }
  if (dateFormat === 'MM/dd') {
    if (!/^\d{2}\/\d{2}$/.test(fieldValue)) return false;
    parts = fieldValue.split('/');
    day = parseInt(parts[1], 10);
    month = parseInt(parts[0], 10);
    if (month === 0 || month > 12) return false;
    // const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day > 0 && day <= monthLength[month - 1];
  }
  return false;
}

function nameRule(fieldValue: string): boolean {
  const regex = /^[a-z\d\-'\s]+$/i;
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    !regex.test(fieldValue)
  );
}

function passwordRule(fieldValue: string): boolean {
  const regex = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!"#$%&'()*+,-./:;<=>?@[\\\]^_'{|}~`]).{8,128}$/;
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    !regex.test(fieldValue)
  );
}

function numericRule(fieldValue: string): boolean {
  const regex = /^\d+$/;
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    !regex.test(fieldValue)
  );
}

function zipCodeRule(fieldValue: string): boolean {
  return numericRule(fieldValue.trim()) && fieldValue.trim().length === 5;
}

function postBoxRule(fieldValue: string): boolean {
  const regex = /^ *((#\d+)|((box|bin)[-. /\\]?\d+)|(.*p[ .]? ?([o0])[-. /\\]? *-?((box|bin)|b|(#|num)?\d+))|(p(ost)? *(o(ff(ice)?)?)? *((box|bin)|b)? *\d+)|(p *-?\/?(o)? *-?box)|post office box|((box|bin)|b) *(number|num|#)? *\d+|(num|number|#) *\d+)/i;
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    regex.test(fieldValue)
  );
}

function cardExpRule(fieldValue: string): boolean {
  const inputMonth = parseInt(fieldValue.split('/')[0], 10);
  const inputYear = parseInt(fieldValue.split('/')[1], 10);
  return Payment.fns.validateCardExpiry(inputMonth, inputYear);
}

function cardNumberRule(fieldValue: string): boolean {
  return fieldValue.length === 0 || Payment.fns.validateCardNumber(fieldValue);
}

function cardTypeRule(fieldValue: string, types: CardType[]): boolean {
  const cardType = Payment.fns.cardType(fieldValue);
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    (cardType === null || !types.includes(cardType.toUpperCase()))
  );
}
function checkForTrueRule(fieldValue: boolean): boolean {
  return fieldValue;
}

function minValueRule(fieldValue: string, minValue: number): boolean {
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    parseFloat(fieldValue.replace('$', '')) < minValue
  );
}

function maxValueRule(fieldValue: string, maxValue: number): boolean {
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.trim() !== '' &&
    parseFloat(fieldValue.replace('$', '')) > maxValue
  );
}

function emptyRule(fieldValue: any[]): boolean {
  return !(
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue.length === 0
  );
}
export default {
  validateComponent,
  isAllValid,
  isValidDate,
  getErrors,
  hasError
};
